import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hrAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ArrowLeft, Edit, Mail, Phone, MapPin, Briefcase, Calendar, DollarSign, ChevronRight, ChevronDown, Shield, Check, X, Bot, Zap, Activity, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { InvalidRoute, isValidId } from '../../components/ui/page-guards';
import { useAsyncAction } from '../../components/ui/action-button';

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Route guard - validate ID before any operations
  if (!isValidId(id)) {
    return (
      <InvalidRoute
        title="Invalid User"
        message="The user ID is missing or invalid. Please select a valid user from the list."
        backPath="/hr/users"
        backLabel="Back to Users"
      />
    );
  }
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  
  // AI Actions state
  const [aiStatus, setAiStatus] = useState(null);
  const [aiActivities, setAiActivities] = useState([]);

  useEffect(() => {
    loadUser();
    loadPermissions();
  }, [id]);

  // Load AI data when user loads
  useEffect(() => {
    if (user?.id) {
      loadAIStatus(user.id);
      loadAIActivities(user.id);
    }
  }, [user?.id]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const response = await hrAPI.getUser(id);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      toast.error('Failed to load user details');
      navigate('/hr/users');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await hrAPI.getPermissions();
      // Convert permissions object to array format
      const permissionsArray = Object.entries(response.data).map(([id, data]) => ({
        id,
        name: data.name,
        submodules: data.submodules ? data.submodules.map(sub => ({
          id: sub,
          name: sub.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        })) : []
      }));
      setPermissions(permissionsArray);
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  };

  // AI Actions handlers
  const loadAIStatus = async (userId) => {
    try {
      const response = await hrAPI.getAIStatus(userId);
      setAiStatus(response.data);
    } catch (error) {
      console.error('Failed to load AI status:', error);
      setAiStatus(null);
    }
  };

  const loadAIActivities = async (userId) => {
    try {
      const response = await hrAPI.getAIActivities(userId);
      setAiActivities(response.data.ai_activities || []);
    } catch (error) {
      console.error('Failed to load AI activities:', error);
      setAiActivities([]);
    }
  };

  const [executeRunHRAI, aiLoading] = useAsyncAction(async () => {
    if (!user?.id) return;
    
    const response = await hrAPI.runHRAI(user.id, { action_type: 'full_playbook' });
    const result = response.data;
    
    if (result.success) {
      const modeLabel = result.mode === 'LIVE' ? 'LIVE' : 'SIMULATED';
      const actionsCount = result.actions_executed?.length || 0;
      toast.success(`HR AI executed (${modeLabel}) — ${actionsCount} action${actionsCount !== 1 ? 's' : ''} sent`, {
        description: result.mode === 'LIVE' ? '🟢 Actions were executed in LIVE mode' : '🟡 Actions were simulated only'
      });
      await loadAIActivities(user.id);
      await loadAIStatus(user.id);
    } else {
      throw new Error(result.blocked_reason || 'HR AI Blocked');
    }
  }, null, 'Failed to execute HR AI');

  const handleRunHRAI = async () => {
    await executeRunHRAI();
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const hasPermission = (moduleId, submoduleId, action) => {
    if (!user?.module_permissions) return false;
    
    // Map 'read' to 'view' for database compatibility
    const dbAction = action === 'read' ? 'view' : action;
    
    if (submoduleId) {
      // Check submodule permission
      return user.module_permissions[moduleId]?.[submoduleId]?.[dbAction] === true;
    } else {
      // Check if any submodule has this permission (for module-level display)
      const modulePerms = user.module_permissions[moduleId];
      if (!modulePerms) return false;
      
      // Check if at least one submodule has the permission
      return Object.values(modulePerms).some(submodule => 
        submodule[dbAction] === true
      );
    }
  };

  const getDepartmentRole = (dept) => {
    if (!user?.department_permissions) return null;
    return user.department_permissions[dept];
  };

  const getDepartmentAccess = (dept) => {
    if (!user?.department_permissions) return false;
    const deptPerms = user.department_permissions[dept];
    return deptPerms?.access === true;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="p-6 space-y-6" data-testid="user-details-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/hr/users')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{user.name}</h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={user.is_active ? 'default' : 'secondary'}>
            {user.is_active ? 'Active' : 'Inactive'}
          </Badge>
          <Button onClick={() => navigate(`/hr/users?edit=${id}`)} data-testid="edit-user-detail-btn">
            <Edit className="h-4 w-4 mr-2" />
            Edit User
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{user.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span>{user.phone || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <span className="capitalize">{user.department?.replace(/_/g, ' ')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="outline">{user.role?.toUpperCase()}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p>{user.address || 'N/A'}</p>
                <p>{user.city}, {user.state}</p>
                <p>{user.country} {user.zipcode}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Employment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Employee ID: {user.employee_id || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Joined: {user.join_date || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span>
                {user.salary_info 
                  ? `${user.salary_info.base_salary} ${user.salary_info.salary_type}`
                  : 'Not set'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* AI Actions Section */}
        <Card className="border-orange-500/30 bg-orange-500/5" data-testid="hr-ai-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bot className="h-4 w-4 text-orange-400" />
              AI Actions
              {/* Execution Mode Indicator */}
              {aiStatus && (
                <Badge 
                  variant="outline" 
                  className={`ml-auto text-xs ${
                    aiStatus.effective_mode === 'LIVE' 
                      ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                      : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                  }`}
                  data-testid="ai-execution-mode-badge"
                >
                  {aiStatus.effective_mode === 'LIVE' ? '🟢 LIVE MODE' : '🟡 SIMULATED MODE'}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Run HR AI Button */}
            <Button
              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              onClick={handleRunHRAI}
              disabled={aiLoading || (aiStatus && !aiStatus.can_execute)}
              data-testid="run-hr-ai-button"
            >
              {aiLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Running HR AI...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Run HR AI
                </>
              )}
            </Button>
            
            {/* Blocked reason */}
            {aiStatus && !aiStatus.can_execute && aiStatus.blocked_reason && (
              <p className="text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded" data-testid="ai-blocked-reason">
                ⚠️ {aiStatus.blocked_reason}
              </p>
            )}
            
            {/* AI Status Info */}
            {aiStatus && (
              <div className="text-xs text-muted-foreground space-y-1">
                <div className="flex justify-between">
                  <span>AI Enabled:</span>
                  <span className={aiStatus.ai_enabled ? 'text-green-400' : 'text-red-400'}>
                    {aiStatus.ai_enabled ? 'Yes' : 'No'}
                  </span>
                </div>
                {aiStatus.kill_switch_active && (
                  <div className="flex justify-between">
                    <span>Kill Switch:</span>
                    <span className="text-red-400">Active</span>
                  </div>
                )}
                {aiStatus.agent_paused && (
                  <div className="flex justify-between">
                    <span>Agent Status:</span>
                    <span className="text-amber-400">Paused</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Recent AI Activities */}
            {aiActivities.length > 0 && (
              <div className="pt-2 border-t border-orange-500/20">
                <p className="text-xs font-medium mb-2 flex items-center gap-1">
                  <Activity className="h-3 w-3" />
                  Recent AI Activity
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {aiActivities.slice(0, 3).map((activity, idx) => (
                    <div key={activity.id || idx} className="text-xs bg-slate-800/50 rounded p-2">
                      <div className="flex items-center justify-between mb-1">
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] ${
                            activity.mode === 'SIMULATED' 
                              ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' 
                              : 'bg-green-500/20 text-green-400 border-green-500/30'
                          }`}
                        >
                          {activity.mode}
                        </Badge>
                        <span className="text-muted-foreground">
                          {activity.created_at ? format(new Date(activity.created_at), 'MMM dd, HH:mm') : '-'}
                        </span>
                      </div>
                      <p className="text-muted-foreground">{activity.summary}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Department Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Department Access & Roles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['sales', 'client', 'account', 'field_service', 'support_service', 'inventory', 'hr'].map(dept => {
              const deptPerms = getDepartmentRole(dept);
              const hasAccess = getDepartmentAccess(dept);
              return (
                <div key={dept} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium capitalize">{dept.replace(/_/g, ' ')}</span>
                    {hasAccess ? (
                      <Badge className="bg-green-500">Enabled</Badge>
                    ) : (
                      <Badge variant="secondary">Disabled</Badge>
                    )}
                  </div>
                  {deptPerms && (
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Role:</span>
                        <Badge variant="outline">{deptPerms.role?.toUpperCase()}</Badge>
                      </div>
                      {deptPerms.show_admin_data && (
                        <div className="flex items-center gap-2 text-green-600">
                          <Check className="h-3 w-3" />
                          <span className="text-xs">Show Admin Data</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Module Permissions Tree */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            Module Permissions
          </CardTitle>
          <div className="flex gap-4 mt-3 text-xs font-medium text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span>Create</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span>Read</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-500"></div>
              <span>Update</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span>Delete</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-3">
            {permissions.map(module => {
              const createPerm = hasPermission(module.id, null, 'create');
              const readPerm = hasPermission(module.id, null, 'read');
              const updatePerm = hasPermission(module.id, null, 'update');
              const deletePerm = hasPermission(module.id, null, 'delete');
              const hasAnyPerm = createPerm || readPerm || updatePerm || deletePerm;
              
              return (
                <div key={module.id} className={`border rounded-xl overflow-hidden transition-all ${hasAnyPerm ? 'border-primary/20 bg-gradient-to-r from-primary/5 to-transparent' : 'border-border'}`}>
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => toggleModule(module.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`transition-transform ${expandedModules[module.id] ? 'rotate-90' : ''}`}>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <span className="font-semibold text-sm">{module.name}</span>
                      {hasAnyPerm && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                          Active
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-3">
                      {[
                        { action: 'create', color: 'bg-green-500', label: 'C' },
                        { action: 'read', color: 'bg-blue-500', label: 'R' },
                        { action: 'update', color: 'bg-amber-500', label: 'U' },
                        { action: 'delete', color: 'bg-red-500', label: 'D' }
                      ].map(({ action, color, label }) => {
                        const permitted = hasPermission(module.id, null, action);
                        return (
                          <div key={action} className="relative group" title={action.charAt(0).toUpperCase() + action.slice(1)}>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                              permitted 
                                ? `${color} text-white shadow-md` 
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                            }`}>
                              {label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {expandedModules[module.id] && module.submodules && module.submodules.length > 0 && (
                    <div className="border-t bg-muted/10">
                      {module.submodules.map(submodule => {
                        const subCreatePerm = hasPermission(module.id, submodule.id, 'create');
                        const subReadPerm = hasPermission(module.id, submodule.id, 'read');
                        const subUpdatePerm = hasPermission(module.id, submodule.id, 'update');
                        const subDeletePerm = hasPermission(module.id, submodule.id, 'delete');
                        
                        return (
                          <div
                            key={submodule.id}
                            className="flex items-center justify-between p-3 pl-16 hover:bg-muted/20 transition-colors border-b last:border-b-0"
                          >
                            <span className="text-sm font-medium text-muted-foreground">{submodule.name}</span>
                            <div className="flex gap-2">
                              {[
                                { action: 'create', color: 'bg-green-500', perm: subCreatePerm },
                                { action: 'read', color: 'bg-blue-500', perm: subReadPerm },
                                { action: 'update', color: 'bg-amber-500', perm: subUpdatePerm },
                                { action: 'delete', color: 'bg-red-500', perm: subDeletePerm }
                              ].map(({ action, color, perm }) => (
                                <div key={action} className="relative" title={action.charAt(0).toUpperCase() + action.slice(1)}>
                                  {perm ? (
                                    <div className={`w-6 h-6 rounded-md ${color} flex items-center justify-center`}>
                                      <Check className="h-3.5 w-3.5 text-white" />
                                    </div>
                                  ) : (
                                    <div className="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                      <X className="h-3.5 w-3.5 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Attendance Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">View attendance history and records</p>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate(`/hr/attendance?user=${id}`)}
            >
              View Attendance
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Salary Records</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">View salary history and payslips</p>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate(`/hr/salary?user=${id}`)}
            >
              View Salary
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Leave Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">View leave requests and balance</p>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate(`/hr/leaves?user=${id}`)}
            >
              View Leaves
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
